/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2025-01-11 12:59:55
 **/

import { ReactNode, Children, PropsWithChildren, useCallback } from "react";
import has from "lodash-es/has";

/**
 * A generic type `TestType` that can represent one of the following:
 * - A value of type `T`
 * - A function that returns a boolean
 * - `null`
 *
 * This type can be used to define variables, parameters, or return types
 * where the value alternates between one of the three specified options.
 *
 * @template T - The type of the primary value.
 */
type TestType<T> = T | (() => boolean) | null;

/**
 * Represents a set of optional properties that might be used for conditional rendering or logic.
 *
 * @template T - The type parameter associated with the `test` property.
 *
 * @property {TestType<T>} [test] - An optional property that defines a condition or value
 *                                  used for evaluation.
 * @property {ReactNode} [fallback] - An optional property representing a fallback element
 *                                     to display when certain conditions are not met.
 * @property {boolean} [hidden] - An optional property indicating whether the element should
 *                                 be hidden or not.
 */
type MaybeProps<T> = {
	test?: TestType<T>;
	fallback?: ReactNode;
	hidden?: boolean;
};

/**
 * Evaluates the input test parameter based on its type.
 * If the input is a function, it invokes the function and returns the result.
 * If the input is not a function, it directly returns the value of the input.
 *
 * @param {TestType<T>} test - The test input which can be either a function or a value.
 * @return {boolean | T | null} Returns the result of the function if the test is a function,
 * the input value if it is not a function, or null based on the input type.
 */
function testing<T>(test: TestType<T>): boolean | T | null {
	if (typeof test === "function") {
		return (test as () => boolean)();
	}
	return test;
}

/**
 * Conditionally renders children components based on a test condition.
 *
 * @param {PropsWithChildren<MaybeProps>} props - The properties object. It should contain 'test', 'children', and optionally 'fallback'.
 * - props.test: A condition to evaluate.
 * - props.children: The child components to conditionally render.
 * - props.fallback: An optional fallback component to render if the condition fails.
 * @return {ReactNode} The rendered components based on the condition.
 */
export default function Maybe<T>(props: PropsWithChildren<MaybeProps<T>>): ReactNode {
	const { hidden = false } = props;
	const condition = (props.test && testing<T>(props.test!)) || false;
	const has_fallback = useCallback(() => {
		let count = 0;
		Children.forEach(props.children, (child) => {
			try {
				// @ts-expect-error ...
				count += child.type === Maybe.Fallback ? 1 : 0;
			} catch {
				// ignoring
			}
		});
		return count > 0;
	}, [props.children]);

	const render_children = useCallback(() => {
		if (has_fallback()) {
			return Children.map(props.children, (child) => {
				try {
					// @ts-expect-error ...
					if ((!condition && child.type !== Maybe.Fallback) || (condition && child.type === Maybe.Fallback)) {
						return null;
					}
					return <>{child}</>;
				} catch {
					return null;
				}
			});
		}
		return condition ? <>{props.children}</> : null;
	}, [condition, has_fallback, props.children]);

	if (hidden) {
		return null;
	}

	if (!has(props, "test")) {
		return <>{props.children}</>;
	}

	if (!condition && !has_fallback()) {
		if (props.fallback) {
			return <>{props.fallback}</>;
		}
	}

	return render_children();
}

/**
 * Class representing a fallback mechanism for a Maybe monad.
 */
Maybe.Fallback = (props: PropsWithChildren) => {
	return <>{props.children}</>;
};
